import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { CreditDebitReportQueryInterface } from '@zaoblako/shared/modules/debts/dtos/credit-debit-report/credit-debit-report-query.dto';
import type { BillFindManyQueryInterface } from '@zaoblako/shared/modules/document/dtos/bill/bill-find-many-query.dto';
import { DocumentFlatQueryInterface } from '@zaoblako/shared/modules/document/dtos/document/document-flat-query.dto';
import { BillPaymentStatusEnum } from '@zaoblako/shared/modules/document/enums/bill.payment.status.enum';
import type { EmailFindInterface } from '@zaoblako/shared/modules/integration/dtos/email/email-find.dto';
import type { MainSmsAccountFindInterface } from '@zaoblako/shared/modules/integration/dtos/main-sms/main-sms-account-find.dto';
import { EmailGateEnum } from '@zaoblako/shared/modules/integration/enums/email.gate.enum';
import { MainSmsAccountTypeEnum } from '@zaoblako/shared/modules/integration/enums/main.sms.account.type.enum';
import { MainSmsIntegrationTypeEnum } from '@zaoblako/shared/modules/integration/enums/main.sms.integration.type.enum';
import { SmsGateEnum } from '@zaoblako/shared/modules/integration/enums/sms.gate.enum';
import { BillTargetItemInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/bill-target-item.dto';
import { DocumentTargetItemInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/document-target-item.dto';
import { MailingFileDebtorTargetFindInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-file-debtor-target-find.dto';
import { MailingFileDebtorTargetFormInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-file-debtor-target-form.dto';
import type { MailingFindInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-find.dto';
import {
  MailingFormDto,
  type MailingFormInterface
} from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-form.dto';
import { MailingAttachmentStatusEnum } from '@zaoblako/shared/modules/notification/enums/mailing-attachment-status.enum';
import { MailingChannelEnum } from '@zaoblako/shared/modules/notification/enums/mailing.channel.enum';
import { MailingTypeEnum } from '@zaoblako/shared/modules/notification/enums/mailing.type.enum';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { BasisMailingBillsService } from '@zaoblako/web/core/services/basis/basis-mailing-bills.service';
import { BasisMailingDocumentsService } from '@zaoblako/web/core/services/basis/basis-mailing-documents.service';
import { BasisNotificationService } from '@zaoblako/web/core/services/basis/basis-notification.service';
import { DashboardService } from '@zaoblako/web/core/services/dashboard/dashboard.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { NewsletterHistoryWidgetComponent } from '@zaoblako/web/modules/debts/dumbs/newsletter-history-widget/newsletter-history-widget.component';
import { NewsletterMailingLimitModalComponent } from '@zaoblako/web/modules/debts/dumbs/newsletter-mailing-limit-modal/newsletter-mailing-limit-modal.component';
import { FileUploaderComponent } from '@zaoblako/web/shared/components/file-uploader/file-uploader.component';
import { GoBackButtonComponent } from '@zaoblako/web/shared/components/go-back-button/go-back-button.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { DaterangepickerComponent } from '@zaoblako/web/shared/dumbs/daterangepicker/daterangepicker.component';
import { ContragentMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contragent-multi-select/contragent-multi-select.component';
import { DirectionMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-multi-select/direction-multi-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { NewsletterEmailMainSmsSenderSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/newsletter-email-main-sms-sender-single-select/newsletter-email-main-sms-sender-single-select.component';
import { NewsletterEmailSenderSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/newsletter-email-sender-single-select/newsletter-email-sender-single-select.component';
import { NewsletterSmsSenderSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/newsletter-sms-sender-single-select/newsletter-sms-sender-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { CompanyMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-multi-select/company-multi-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { BillPaymentStatusNamePipe } from '@zaoblako/web/shared/pipes/bill-payment-status-name.pipe';
import { FindByKeyPipe } from '@zaoblako/web/shared/pipes/find-by-key.pipe';
import { JoinWithChildPipe } from '@zaoblako/web/shared/pipes/join-with-child.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { MailingAttachmentStatusNamePipe } from '@zaoblako/web/shared/pipes/mailing-attachment-status-name.pipe';
import { MailingChannelNamePipe } from '@zaoblako/web/shared/pipes/mailing-channel-name.pipe';
import { MailingTypeNamePipe } from '@zaoblako/web/shared/pipes/mailing-type-name.pipe';
import { PhonePipe } from '@zaoblako/web/shared/pipes/phone.pipe';
import moment from 'moment-timezone';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DividerModule } from 'primeng/divider';

@Component({
  standalone: true,
  selector: 'crm-newsletter-form',
  templateUrl: './newsletter-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    EditorComponent,
    SingleSelectComponent,
    MailingTypeNamePipe,
    MailingChannelNamePipe,
    MultiSelectComponent,
    FileUploaderComponent,
    CheckboxModule,
    FormsModule,
    DatepickerComponent,
    JoinWithChildPipe,
    TableComponent,
    PhonePipe,
    NewsletterHistoryWidgetComponent,
    ButtonComponent,
    GoBackButtonComponent,
    NewsletterSmsSenderSingleSelectComponent,
    NewsletterEmailSenderSingleSelectComponent,
    SelectButtonModule,
    NewsletterEmailMainSmsSenderSingleSelectComponent,
    DaterangepickerComponent,
    DirectionMultiSelectComponent,
    BillPaymentStatusNamePipe,
    FindByKeyPipe,
    RouterLink,
    UserMultiSelectComponent,
    UserGroupMultiSelectComponent,
    ContragentMultiSelectComponent,
    MailingAttachmentStatusNamePipe,
    DividerModule,
    CompanyMultiSelectComponent
  ],
  providers: [DashboardService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsletterFormComponent extends BaseFormComponent<MailingFormInterface> implements OnDestroy {
  @Input() public data = <MailingFormInterface>{};
  public dto = MailingFormDto;

  @Input() public emails: EmailFindInterface[] = [];
  @Input() public arraySms: MainSmsAccountFindInterface[] = [];
  @Input() public externalUsers: ExternalUserFindInterface[] = [];
  @Input() public recipientUsers: UserFindInterface[] = [];
  @Input() public directions: DirectionFindInterface[] = [];

  public bills: BillTargetItemInterface[] = [];
  public documents: DocumentTargetItemInterface[] = [];

  public loadingBills = false;
  public loadingDocuments = false;

  public types: MailingTypeEnum[] = [];

  public channels: MailingChannelEnum[] = Object.values(MailingChannelEnum);
  public billingPaymentStatuses: BillPaymentStatusEnum[] = Object.values(BillPaymentStatusEnum);
  public attachmentStatuses: MailingAttachmentStatusEnum[] = Object.values(MailingAttachmentStatusEnum);

  public emailOptions: { label: string; value: EmailGateEnum }[] = [
    { label: 'Почта', value: EmailGateEnum.FRM },
    { label: 'MainSMS', value: EmailGateEnum.MAIN_SMS }
  ];

  public loadingFile = false;

  public columns: CrmTableColumn[] = [
    {
      label: 'Контрагент',
      name: 'contragent',
      sort: 'name:string',
      style: { 'min-width.px': 140 }
    },
    { label: 'Контакт', style: { 'min-width.px': 140 } },
    { label: 'Сумма', name: 'amount', sort: 'amount:number', style: { 'min-width.px': 100, 'max-width.px': 100 } },
    { style: { 'max-width.px': 35, 'min-width.px': 35 } }
  ];

  public billColumns: CrmTableColumn[] = [
    { label: 'Номер', name: 'erp1cNumber', sort: 'erp1cNumber:string' },
    { label: 'Контрагент', name: 'contragent', sort: 'contragent.name:string' },
    { label: 'Бюджетные статьи', name: 'direction' },
    { label: 'Сумма', name: 'amount', sort: 'amount:number' },
    { label: 'Состояние', name: 'lastSentViaMailing', sort: 'lastSentViaMailing:date' }
  ];

  public documentColumns: CrmTableColumn[] = [
    { label: 'Номер', name: 'externalCode', sort: 'externalCode:string' },
    { label: 'Контрагент', name: 'contragent', sort: 'contragent.name:string' },
    { label: 'Сумма', name: 'amount', sort: 'amount:number' },
    { label: 'Состояние', name: 'lastSentViaMailing', sort: 'lastSentViaMailing:date' }
  ];

  private ref: DynamicDialogRef;

  public get getNewsletterId() {
    return (this.data as MailingFindInterface)?.id;
  }

  public get MailingTypeEnum() {
    return MailingTypeEnum;
  }

  public get MailingChannelEnum() {
    return MailingChannelEnum;
  }

  public get EmailGateEnum() {
    return EmailGateEnum;
  }

  public constructor(
    private readonly dashboardService: DashboardService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly basisNotificationService: BasisNotificationService,
    private readonly basisMailingBillsService: BasisMailingBillsService,
    private readonly basisMailingDocumentsService: BasisMailingDocumentsService,
    private readonly accessService: AccessService,
    private readonly dialogService: DialogService,
    private readonly errorService: ErrorService
  ) {
    super();

    this.types = Object.values(MailingTypeEnum).filter((type) => {
      if (!this.accessService.debts?.debtsRights.canReadBillRegistry) {
        return type !== MailingTypeEnum.BILLS;
      }
      return true;
    });

    if (!this.data.date) {
      this.data.date = new Date();
    }

    const rights = this.accessService.finance.documentRights;
    this.types = this.types.filter((type) => {
      if (type === MailingTypeEnum.DOCUMENTS) {
        return rights.canReadDocument;
      }
      return true;
    });

    const isSelectedTypeAllowed = this.types.some((type) => type === this.data.type);
    if (!isSelectedTypeAllowed) {
      this.data.type = MailingTypeEnum.DEBTORS;
    }
  }

  public ngOnDestroy() {
    this.ref?.close();
  }

  public onSave(entity: MailingFormInterface) {
    const notificationModal = () => {
      let type: MainSmsIntegrationTypeEnum;
      if (this.data.channel === MailingChannelEnum.EMAIL) {
        type = MainSmsIntegrationTypeEnum.EMAIL;
      } else if (this.data.channel === MailingChannelEnum.SMS) {
        type = MainSmsIntegrationTypeEnum.SMS;
      }

      this.ref = this.dialogService.open(NewsletterMailingLimitModalComponent, {
        header: 'Уведомление',
        showHeader: false,
        contentStyle: { overflow: 'auto', 'border-radius': '0.5rem' },
        styleClass: 'xl:w-5 lg:w-6 md:w-8 sm:w-11 w-full',
        focusOnShow: false,
        baseZIndex: 10000,
        data: {
          type: type
        }
      });

      this.ref.onClose.subscribe((sms: MainSmsAccountFindInterface) => {
        if (sms) {
          this.arraySms.push(sms);
          if (sms.type === MainSmsIntegrationTypeEnum.SMS) {
            entity.smsSender.mainSmsAccount = sms;
            this.cdRef.markForCheck();
            super.onSave(entity);
          } else if (sms.type === MainSmsIntegrationTypeEnum.EMAIL) {
            entity.emailSender.mainSmsAccount = sms;
            setTimeout(() => {
              entity.emailSender.mainSmsAccountEmail = sms.emails[0];
              this.cdRef.markForCheck();
              super.onSave(entity);
            }, 100);
          }
        }
      });
    };

    const checkLimit = (countContacts: number) => {
      if (
        (this.data.channel === MailingChannelEnum.EMAIL &&
          this.data.emailSender?.gate === EmailGateEnum.MAIN_SMS &&
          this.data.emailSender?.mainSmsAccount?.accountType === MainSmsAccountTypeEnum.TEMPORARY &&
          this.data.emailSender?.mainSmsAccount?.emailMessagesAvailable < countContacts) ||
        (this.data.channel === MailingChannelEnum.SMS &&
          this.data.smsSender?.mainSmsAccount?.accountType === MainSmsAccountTypeEnum.TEMPORARY &&
          this.data.smsSender?.mainSmsAccount?.smsMessagesAvailable < countContacts)
      ) {
        return notificationModal();
      }
      super.onSave(entity);
    };

    if (this.data.type === MailingTypeEnum.DEBTORS) {
      let countContacts = 0;
      const queryParams: CreditDebitReportQueryInterface = {
        date: this.data.includingAdvancePayment
          ? (moment(this.data.date).endOf('month').format('YYYY-MM-DD') as unknown as Date)
          : (moment(this.data.date).format('YYYY-MM-DD') as unknown as Date),
        companyIds: this.data.companies?.map((company) => company.id),
        onlyPositive: true
      };
      this.dashboardService.getDebtors(queryParams).subscribe((data) => {
        countContacts = data.items.reduce((sum, item) => sum + item.externalUsersMailingEnabledCount || 0, 0);
        checkLimit(countContacts);
      });
    } else if (this.data.type === MailingTypeEnum.DEBTORS_FROM_FILE) {
      checkLimit(this.data.fileDebtorTargets.length);
    } else {
      super.onSave(entity);
    }
  }

  public deleteContragentFromFile(debtorTarget: MailingFileDebtorTargetFormInterface) {
    this.data.fileDebtorTargets = this.data.fileDebtorTargets.filter((target) => target !== debtorTarget);
  }

  public toItemContactFromFile(contact: any): MailingFileDebtorTargetFormInterface {
    return contact as MailingFileDebtorTargetFormInterface;
  }

  public selectedFileDebtor(files: File[]) {
    if (files) {
      const formData = new FormData();
      formData.append('file', files[0], files[0].name);

      this.loadingFile = true;
      this.cdRef.markForCheck();

      this.basisNotificationService.debtorTargetsFromExcel(formData).subscribe({
        next: (result) => {
          this.data.fileDebtorTargets = result;
          this.loadingFile = false;
          this.cdRef.markForCheck();
        },
        error: (error: HttpErrorResponse) => {
          let str = 'Некорректно заполнены данные:';
          error.error?.validationErrors.forEach((item: MailingFileDebtorTargetFindInterface, index: number) => {
            if (item) {
              Object.keys(item).forEach((key: keyof MailingFileDebtorTargetFindInterface) => {
                if (item[key]) {
                  let char: string;
                  switch (key) {
                    case 'name': {
                      char = 'A';
                      break;
                    }
                    case 'phone': {
                      char = 'B';
                      break;
                    }
                    case 'email': {
                      char = 'C';
                      break;
                    }
                    case 'amount': {
                      char = 'D';
                      break;
                    }
                  }
                  str = str.concat(`\n- Ячейка ${char}${index + 2}: ${item[key]};`);
                }
              });
            }
          });
          this.errorService.addCustomError('Ошибка валидации', str);
          this.loadingFile = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public changeChannel(channel: MailingChannelEnum) {
    if (channel === MailingChannelEnum.EMAIL) {
      this.data.smsSender = null;
      this.data.emailSender = {
        gate: EmailGateEnum.FRM
      };
      if (this.emails?.length === 1) {
        this.data.emailSender.email = this.emails[0];
      }
    } else if (channel === MailingChannelEnum.SMS) {
      this.data.emailSender = null;
      if (this.arraySms.filter((sms) => sms.type === MainSmsIntegrationTypeEnum.SMS)?.length === 1) {
        this.data.smsSender = {
          gate: SmsGateEnum.MAIN_SMS,
          mainSmsAccount: this.arraySms.filter((sms) => sms.type === MainSmsIntegrationTypeEnum.SMS)[0]
        };
      }
    } else {
      this.data.emailSender = null;
      this.data.smsSender = null;
    }
  }

  public changeEmailType(type: EmailGateEnum) {
    if (type === EmailGateEnum.MAIN_SMS) {
      this.data.emailSender.email = null;
      if (this.arraySms.filter((sms) => sms.type === MainSmsIntegrationTypeEnum.EMAIL)?.length === 1) {
        this.data.emailSender.mainSmsAccount = this.arraySms.filter(
          (sms) => sms.type === MainSmsIntegrationTypeEnum.EMAIL
        )[0];
        if (this.data.emailSender.mainSmsAccount?.emails?.length === 1) {
          setTimeout(() => {
            this.data.emailSender.mainSmsAccountEmail = this.data.emailSender.mainSmsAccount.emails[0];
            this.cdRef.markForCheck();
          }, 100);
        }
      }
    } else {
      this.data.emailSender.mainSmsAccountEmail = '';
      this.data.emailSender.mainSmsAccount = null;
      if (this.emails?.length === 1) {
        this.data.emailSender.email = this.emails[0];
      }
    }
  }

  public changeEmailMainSms(email: MainSmsAccountFindInterface) {
    this.data.emailSender.mainSmsAccountEmail = '';
    if (email?.emails?.length === 1) {
      setTimeout(() => {
        this.data.emailSender.mainSmsAccountEmail = email.emails[0];
        this.cdRef.markForCheck();
      }, 100);
    }
  }

  public setBillingPeriod(dates: [Date, Date]) {
    this.data.attachmentStartDate = dates[0];
    this.data.attachmentEndDate = dates[1];
  }

  public toggleAllSelectedBill() {
    if (!this.data.bills || !Array.isArray(this.data.bills)) {
      this.data.bills = [];
    }
    if (this.data.bills?.length !== this.bills?.length) {
      this.data.bills = this.bills;
    } else {
      this.data.bills = [];
    }
    this.cdRef.markForCheck();
  }

  public toggleSelectedBill(bill: BillTargetItemInterface) {
    if (!this.data.bills || !Array.isArray(this.data.bills)) {
      this.data.bills = [];
    }
    if (this.data.bills.some((selectedBill) => selectedBill.erp1cRef === bill?.erp1cRef)) {
      this.data.bills = this.data.bills.filter((selectedBill) => selectedBill.erp1cRef !== bill?.erp1cRef);
    } else {
      this.data.bills.push(bill);
      this.data.bills = [...this.data.bills];
      this.cdRef.markForCheck();
    }
  }

  public toBill(bill: any): BillTargetItemInterface {
    return bill as BillTargetItemInterface;
  }

  public findBills() {
    this.loadingBills = true;
    this.cdRef.markForCheck();

    this.basisMailingBillsService
      .find<BillTargetItemInterface>(<BillFindManyQueryInterface>{
        paymentStatuses: this.data.billingPaymentStatuses || [],
        mailingAttachmentStatuses: this.data.attachmentStatuses || [],
        directionIds: this.data.billingDirections?.map((direction) => direction.id) || [],
        from: moment(this.data.attachmentStartDate).format('YYYY-MM-DD') as unknown as Date,
        to: moment(this.data.attachmentEndDate).format('YYYY-MM-DD') as unknown as Date,
        companyIds: this.data.companies?.map((company) => company.id) || [],
        contragentIds: this.data.contragents?.map((contragent) => contragent.id) || [],
        select: [...this.billColumns.map((column) => column.name), 'erp1cRef']
      })
      .subscribe({
        next: (bills) => {
          this.bills = bills;
          if (!this.bills?.length) {
            this.errorService.addCustomError('Нет счетов с такими фильтрами');
          }
          this.loadingBills = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loadingBills = false;
          this.cdRef.markForCheck();
        }
      });
  }

  public findDocuments() {
    this.loadingDocuments = true;
    this.cdRef.markForCheck();

    this.basisMailingDocumentsService
      .find<DocumentTargetItemInterface>(<DocumentFlatQueryInterface>{
        from: moment(this.data.attachmentStartDate).format('YYYY-MM-DD') as unknown as Date,
        to: moment(this.data.attachmentEndDate).format('YYYY-MM-DD') as unknown as Date,
        companyIds: this.data.companies?.map((company) => company.id) || [],
        contragentIds: this.data.contragents?.map((contragent) => contragent.id) || [],
        mailingAttachmentStatuses: this.data.attachmentStatuses || [],
        documentTypeCodes: ['S'],
        select: this.documentColumns.map((column) => column.name)
      })
      .subscribe({
        next: (documents) => {
          this.documents = documents;
          if (!this.documents?.length) {
            this.errorService.addCustomError('Нет документов с такими фильтрами!');
          }
          this.loadingDocuments = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loadingDocuments = false;
          this.cdRef.markForCheck();
        }
      });
  }

  public toDocument(document: any): DocumentTargetItemInterface {
    return document as DocumentTargetItemInterface;
  }

  public toggleAllSelectedDocuments() {
    if (!this.data.documents || !Array.isArray(this.data.documents)) {
      this.data.documents = [];
    }
    if (this.data.documents?.length !== this.documents?.length) {
      this.data.documents = this.documents;
    } else {
      this.data.documents = [];
    }
    this.cdRef.markForCheck();
  }

  public toggleSelectedDocument(document: DocumentTargetItemInterface) {
    if (!this.data.documents || !Array.isArray(this.data.documents)) {
      this.data.documents = [];
    }
    if (this.data.documents.some((selectedItem) => selectedItem.erp1cRef === document?.erp1cRef)) {
      this.data.documents = this.data.documents.filter((selectedItem) => selectedItem.erp1cRef !== document?.erp1cRef);
    } else {
      this.data.documents.push(document);
      this.data.documents = [...this.data.documents];
      this.cdRef.markForCheck();
    }
  }

  public toData(data: any): MailingFormInterface {
    return data as MailingFormInterface;
  }
}
